import {
    type ChangeEventHandler,
    type ComponentRef,
    useCallback,
    useEffect,
    useReducer,
    useRef,
} from 'react';
import '../../../css/importExport.css';
import { getAllEnvironments } from '../sources/environments';
import translations from '../translations';
import type { Environment } from '../types/environment';
import type { FlowResponseAPI, PackageConflictResponse } from '../types/flow';
import { isNullOrUndefined } from '../utils/guard';
import { stringReplace } from '../utils/string';
import ButtonPrimary from './buttons/ButtonPrimary';
import DependencyTable from './generic/DependencyTable';
import FormGroup from './generic/FormGroup';
import ConfirmModal from './generic/modal/ConfirmModal';
import ComponentWithTooltip from './generic/tooltip/ComponentWithTooltip';
import FileDrop from './generic/upload/FileDrop';
import ClipboardInput from './inputs/ClipboardInput';
import Loader from './loader/Loader';
import { useNotificationDispatch } from './notifications/NotificationProvider';
import { useTabs, useTabsDispatch } from './tabs/TabsProvider';
import ThemeImportExport from './theme/ThemeImportExport';
import TranslationBase from './translations/TranslationsBase';
import { useAuth } from './AuthProvider';
import {
    exportFlow,
    getActivated,
    getActivatedForEnvironment,
    importFlow,
    importSharedFlow,
    shareFlow,
} from '../sources/flow';
import { activateFileDownload } from '../utils/file';

const ImportExport = () => {
    const [state, dispatchImportExport] = useReducer(reducer, {
        selectedEnvironmentId: null,
        environments: [],
        flows: [],
        selectedFlowId: null,
        importedJSON: null,
        importToken: null,
        sharingToken: null,
        includePasswords: false,
        conflictedImportDependents: null,
        showImportConfirmation: false,
        isProcessing: false,
    });
    const {
        selectedEnvironmentId,
        environments,
        flows,
        selectedFlowId,
        importedJSON,
        importToken,
        sharingToken,
        includePasswords,
        conflictedImportDependents,
        showImportConfirmation,
        isProcessing,
    } = state;
    const modalContainerRef = useRef<ComponentRef<'div'>>(null);
    const { tenant } = useAuth();
    const { tabs } = useTabs();
    const dispatchTabs = useTabsDispatch();
    const dispatchNotification = useNotificationDispatch();

    const fetchActivatedFlows = useCallback(
        async (environmentId: string | null) => {
            try {
                let activatedFlows: FlowResponseAPI[] = [];

                if (environmentId) {
                    activatedFlows = await getActivatedForEnvironment(environmentId);
                } else {
                    activatedFlows = await getActivated();
                }

                dispatchImportExport({
                    type: 'flowsLoaded',
                    payload: activatedFlows,
                });
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: { error },
                });
            }
        },
        [dispatchNotification],
    );

    const onChangeImportToken: ChangeEventHandler<HTMLInputElement> = ({ target: { value } }) => {
        dispatchImportExport({
            type: 'importTokenUpdated',
            payload: value,
        });
    };

    const onEnvironmentSelected: ChangeEventHandler<HTMLSelectElement> = ({
        target: { value },
    }) => {
        dispatchImportExport({
            type: 'environmentSelected',
            payload: value,
        });

        if (tenant !== null) {
            fetchActivatedFlows(value);
        }
    };

    const onFlowSelected: ChangeEventHandler<HTMLSelectElement> = ({ target: { value } }) => {
        dispatchImportExport({
            type: 'flowSelected',
            payload: value,
        });
    };

    const onShareFlow = async () => {
        if (!selectedFlowId) {
            return;
        }

        dispatchImportExport({
            type: 'setIsProcessing',
            payload: true,
        });

        try {
            const { token } = await shareFlow(
                selectedFlowId,
                includePasswords,
                selectedEnvironmentId,
            );
            dispatchImportExport({
                type: 'sharingTokenLoaded',
                payload: token,
            });
        } catch (error) {
            dispatchImportExport({
                type: 'sharingTokenFailed',
            });
            dispatchNotification({
                type: 'notifyError',
                payload: { error },
            });
        }
    };

    const onExportFlow = async () => {
        if (!selectedFlowId) {
            return;
        }

        dispatchImportExport({
            type: 'setIsProcessing',
            payload: true,
        });

        try {
            const flowJSON = await exportFlow(
                selectedFlowId,
                includePasswords,
                selectedEnvironmentId,
            );

            const flowName = getNameFromFlowPackage(flowJSON);

            activateFileDownload(
                new Blob([flowJSON], { type: 'application/json' }),
                `${flowName ?? 'Flow'}.package`,
            );
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: { error },
            });
        }

        dispatchImportExport({
            type: 'setIsProcessing',
            payload: false,
        });
    };

    const onImportConfirmed = async (force = false) => {
        if (!importedJSON && !importToken) {
            return;
        }

        try {
            dispatchImportExport({
                type: 'setIsProcessing',
                payload: true,
            });

            const result = importedJSON
                ? await importFlow(importedJSON, force)
                : importToken
                  ? await importSharedFlow(importToken, force)
                  : null;

            if (!result) {
                return;
            }

            dispatchImportExport({
                type: 'dismissImportConfirmation',
            });

            // Check for an open tab tab that has a matching id and ensure the name is correct
            // An import will rename a flow if the ids match but the names are different
            const existingTab = tabs.find(
                (tab) => tab.type === 'flow' && tab.elementId === result.id.id,
            );

            if (existingTab && existingTab.title !== result.developerName) {
                const { key } = existingTab;
                dispatchTabs({
                    type: 'SET_TITLE',
                    payload: { tabKey: key, title: result.developerName },
                });
            }

            dispatchNotification({
                type: 'notifySuccess',
                payload: {
                    message: stringReplace(
                        translations.IMPORT_EXPORT_import_success_message,
                        result.developerName,
                    ),
                },
            });
        } catch (error) {
            const conflict = error as {
                cause: {
                    wasConflict: boolean;
                    conflictingDependents: PackageConflictResponse;
                };
            };
            if (conflict.cause?.wasConflict) {
                dispatchImportExport({
                    type: 'importConflictResponse',
                    payload: conflict.cause.conflictingDependents,
                });
            } else {
                dispatchNotification({
                    type: 'notifyError',
                    payload: { error },
                });
            }
        }
    };

    const onDrop = (files: File[]) => {
        for (const file of files) {
            const reader = new FileReader();
            reader.onloadend = (event) => {
                dispatchImportExport({
                    type: 'fileDropped',
                    payload: event.target?.result?.toString() ?? null,
                });
            };
            reader.readAsText(file);
        }
    };

    const onIncludePasswordsChange: React.ChangeEventHandler<HTMLInputElement> = ({
        target: { checked },
    }) => {
        dispatchImportExport({
            type: 'setIncludePasswords',
            payload: checked,
        });
    };

    const environmentOptions = [
        <option value="" key="no environment">
            {translations.ENVIRONMENT_filters_select_environment}
        </option>,
    ].concat(
        environments.map((environment) => (
            <option value={environment.id} key={environment.id}>
                {environment.name}
            </option>
        )),
    );

    const flowOptions = [
        <option value="" key="no flow">
            {translations.IMPORT_EXPORT_default_flow_option_label}
        </option>,
    ].concat(
        flows.map((flow) => (
            <option value={flow.id.id} key={flow.id.id}>
                {flow.developerName}
            </option>
        )),
    );

    const isFlowSelected = selectedFlowId !== null && selectedFlowId !== '';

    const importedFlowName = getNameFromFlowPackage(importedJSON);

    const confirmFlowImportMessages = [
        isNullOrUndefined(importedFlowName) === false
            ? stringReplace(
                  // If there is a Flow name found, then show the flow name import text
                  translations.IMPORT_confirm_message_with_flow_name,
                  importedFlowName,
                  tenant!.developerName,
              )
            : stringReplace(
                  // Otherwise show the import text without the flow name
                  translations.IMPORT_confirm_message,
                  tenant!.developerName,
              ),
    ];

    const dependencyList = conflictedImportDependents ? (
        <DependencyTable dependents={conflictedImportDependents} />
    ) : null;

    const confirmOverwriteMessages = [
        translations.IMPORT_confirm_overwrite_body_1,
        translations.IMPORT_confirm_overwrite_body_2,
        dependencyList,
        translations.IMPORT_confirm_overwrite_body_3,
        translations.IMPORT_confirm_overwrite_body_4,
    ];

    useEffect(() => {
        (async () => {
            if (tenant?.tenantSettings?.environments) {
                const environments = await getAllEnvironments();
                dispatchImportExport({
                    type: 'environmentsLoaded',
                    payload: environments,
                });
            } else {
                fetchActivatedFlows(null);
            }
        })();
    }, [fetchActivatedFlows, tenant?.tenantSettings?.environments]);

    if (!tenant) {
        return null;
    }

    return (
        <div className="import-export" ref={modalContainerRef}>
            {isProcessing && <Loader message={translations.IMPORT_EXPORT_loading_message} />}
            <h1>{translations.IMPORT_EXPORT_export_section_heading}</h1>
            <div className="export">
                <div
                    className="form-group"
                    hidden={!tenant?.tenantSettings?.environments}
                    data-testid="environmentSelectGroup"
                >
                    <select
                        onChange={onEnvironmentSelected}
                        className="select-flow form-control"
                        data-testid="environmentSelect"
                    >
                        {environmentOptions}
                    </select>
                </div>
                <div className="form-group">
                    <select
                        value={selectedFlowId || ''}
                        onChange={onFlowSelected}
                        className="select-flow form-control"
                        disabled={!!tenant?.tenantSettings?.environments && !selectedEnvironmentId}
                        data-testid="flowSelect"
                    >
                        {flowOptions}
                    </select>
                </div>
                <div className="form-group">
                    <ComponentWithTooltip
                        trigger={['hover', 'focus']}
                        tooltipPlacement="bottom-start"
                        tooltipContent={
                            translations.IMPORT_EXPORT_include_passwords_checkbox_description
                        }
                        tooltipClass="import-export-tooltip"
                        tooltipArrowClass="import-export-tooltip-arrow"
                        fadeTime={0.2}
                        arrowClearance={8}
                    >
                        <label>
                            <input
                                type="checkbox"
                                checked={includePasswords}
                                onChange={onIncludePasswordsChange}
                            />
                            {translations.IMPORT_EXPORT_include_passwords_checkbox_label}
                        </label>
                    </ComponentWithTooltip>
                </div>
                <ButtonPrimary
                    onClick={onShareFlow}
                    disabled={!isFlowSelected || isProcessing}
                    className="share"
                    title={translations.IMPORT_EXPORT_generate_sharing_token}
                >
                    {translations.IMPORT_EXPORT_generate_sharing_token}
                </ButtonPrimary>
                <ButtonPrimary
                    onClick={onExportFlow}
                    disabled={!isFlowSelected || isProcessing}
                    title={translations.IMPORT_EXPORT_download_package}
                >
                    {translations.IMPORT_EXPORT_download_package}
                </ButtonPrimary>
                <div>
                    <h2>{translations.IMPORT_EXPORT_sharing_token_section_heading}</h2>
                    <FormGroup label={translations.IMPORT_EXPORT_sharing_token_label}>
                        <ClipboardInput
                            value={sharingToken || ''}
                            placeholder={translations.IMPORT_EXPORT_sharing_token_placeholder}
                            inputClassName="sharing-token form-control with-extension"
                        />
                    </FormGroup>
                </div>
            </div>
            <div className="import margin-top-large">
                <h1>{translations.IMPORT_EXPORT_import_section_heading}</h1>
                <FileDrop
                    onChange={onDrop}
                    placeholder={translations.IMPORT_EXPORT_file_drop_placeholder}
                    fileTypes=".package"
                />
                <h2>{translations.IMPORT_EXPORT_import_shared_package_section_heading}</h2>
                <p>{translations.IMPORT_EXPORT_sharing_token_input_description}</p>
                <div className="shared">
                    <input
                        type="text"
                        className="form-control import-token"
                        onChange={onChangeImportToken}
                        placeholder={translations.IMPORT_EXPORT_shared_token_input_placeholder}
                        value={importToken || ''}
                    />
                    <ButtonPrimary
                        disabled={!importToken}
                        onClick={() =>
                            dispatchImportExport({
                                type: 'importSharingTokenClicked',
                            })
                        }
                    >
                        {translations.IMPORT_EXPORT_import_button_label}
                    </ButtonPrimary>
                </div>
            </div>
            {tenant?.tenantSettings?.themes && (
                <ThemeImportExport
                    environmentOptions={environmentOptions}
                    tenant={tenant}
                    container={modalContainerRef.current}
                />
            )}
            <TranslationBase
                container={modalContainerRef.current}
                setIsProcessing={(isProcessing: boolean) =>
                    dispatchImportExport({ type: 'setIsProcessing', payload: isProcessing })
                }
            />
            <ConfirmModal
                title={translations.IMPORT_confirm_title}
                messages={confirmFlowImportMessages}
                onCancel={() => dispatchImportExport({ type: 'dismissImportConfirmation' })}
                onConfirm={() => onImportConfirmed(false)}
                show={showImportConfirmation && conflictedImportDependents === null}
                container={modalContainerRef.current}
            />
            <ConfirmModal
                title={translations.IMPORT_confirm_overwrite_title}
                messages={confirmOverwriteMessages}
                onCancel={() => dispatchImportExport({ type: 'dismissImportConfirmation' })}
                onConfirm={() => onImportConfirmed(true)}
                show={conflictedImportDependents !== null}
                container={modalContainerRef.current}
            />
        </div>
    );
};

export default ImportExport;

function getNameFromFlowPackage(flowJSON: string | null) {
    if (!flowJSON) {
        return null;
    }

    try {
        let flowPackage: { flowConfiguration: { developerName: string } } | string =
            JSON.parse(flowJSON);

        // flowJSON may be double encoded, so need to check to see if it is a string or an object
        if (typeof flowPackage === 'string') {
            flowPackage = JSON.parse(flowPackage);
        }

        if (
            typeof flowPackage === 'object' &&
            Object.hasOwn(flowPackage, 'flowConfiguration') &&
            Object.hasOwn(flowPackage.flowConfiguration, 'developerName')
        ) {
            return flowPackage.flowConfiguration.developerName;
        }
    } catch {
        return null;
    }
    return null;
}

function reducer(state: State, action: Action): State {
    switch (action.type) {
        case 'environmentsLoaded':
            return {
                ...state,
                environments: action.payload,
            };
        case 'environmentSelected':
            return {
                ...state,
                selectedEnvironmentId: action.payload,
                selectedFlowId: null,
                flows: [],
            };
        case 'flowsLoaded':
            return {
                ...state,
                flows: action.payload,
            };
        case 'flowSelected':
            return {
                ...state,
                selectedFlowId: action.payload,
            };
        case 'fileDropped':
            return {
                ...state,
                importedJSON: action.payload,
                showImportConfirmation: true,
            };
        case 'importTokenUpdated':
            return {
                ...state,
                importToken: action.payload,
            };
        case 'sharingTokenLoaded':
            return {
                ...state,
                isProcessing: false,
                sharingToken: action.payload,
            };
        case 'sharingTokenFailed':
            return {
                ...state,
                isProcessing: false,
                sharingToken: null,
            };
        case 'setIncludePasswords':
            return {
                ...state,
                includePasswords: action.payload,
            };
        case 'importConflictResponse':
            return {
                ...state,
                conflictedImportDependents: action.payload,
            };
        case 'importSharingTokenClicked':
            return {
                ...state,
                showImportConfirmation: true,
            };
        case 'dismissImportConfirmation':
            return {
                ...state,
                showImportConfirmation: false,
                conflictedImportDependents: null,
                importedJSON: null,
                isProcessing: false,
            };
        case 'setIsProcessing':
            return {
                ...state,
                isProcessing: action.payload,
            };

        default:
            return state;
    }
}

type State = {
    environments: Environment[];
    selectedEnvironmentId: string | null;
    flows: FlowResponseAPI[];
    selectedFlowId: string | null;
    importedJSON: string | null;
    importToken: string | null;
    sharingToken: string | null;
    includePasswords: boolean;
    conflictedImportDependents: PackageConflictResponse | null;
    showImportConfirmation: boolean;
    isProcessing: boolean;
};

type Action =
    | {
          type: 'setIsProcessing';
          payload: boolean;
      }
    | {
          type: 'environmentsLoaded';
          payload: Environment[];
      }
    | {
          type: 'environmentSelected';
          payload: string | null;
      }
    | {
          type: 'flowsLoaded';
          payload: FlowResponseAPI[];
      }
    | {
          type: 'flowSelected';
          payload: string | null;
      }
    | {
          type: 'fileDropped';
          payload: string | null;
      }
    | {
          type: 'importTokenUpdated';
          payload: string | null;
      }
    | {
          type: 'sharingTokenLoaded';
          payload: string | null;
      }
    | {
          type: 'sharingTokenFailed';
      }
    | {
          type: 'setIncludePasswords';
          payload: boolean;
      }
    | {
          type: 'importConflictResponse';
          payload: PackageConflictResponse;
      }
    | {
          type: 'setImportSuccess';
          payload: FlowResponseAPI | PackageConflictResponse | boolean;
      }
    | {
          type: 'importSharingTokenClicked';
      }
    | {
          type: 'dismissImportConfirmation';
      };
